/**
 * Kakuat Local Database (IndexedDB via Dexie)
 *
 * This is the "offline-first" brain of the app.
 * All punches are stored here FIRST, then synced to server.
 *
 * Benefits:
 * - Works in tunnels/basements (no network needed)
 * - Instant UI feedback (optimistic updates)
 * - Survives page refreshes and app crashes
 * - Much larger than localStorage (100MB+ vs 5MB)
 */
import Dexie, { Table } from 'dexie';

// =============================================================================
// Types
// =============================================================================

// `discarded` = dead-letter: agotó MAX_SYNC_INTENTOS subidas rechazadas por
// el servidor y dejó de reintentar (ronda 3, r2b18). NO se borra: queda en
// IndexedDB para auditoría, con su syncError visible y reintentable a mano.
// No hace falta tocar el esquema de Dexie: `syncStatus` es un índice sobre
// valores, y un valor nuevo es dato, no índice.
export type SyncStatus = 'pending' | 'syncing' | 'synced' | 'error' | 'discarded';

/**
 * Cuántas subidas rechazadas por el SERVIDOR tolera un fichaje antes de
 * pasar a «descartado». Con el lote todo-o-nada de `/sync/batch`, un solo
 * evento que el schema rechace (422) tumba el lote entero en cada intento:
 * sin tope, se reenviaba cada 30 s para siempre y envenenaba la cola de
 * TODOS los fichajes del usuario (ronda 3, r2b18).
 */
export const MAX_SYNC_INTENTOS = 5;

/**
 * Dónde se trabaja el tiempo que marca este fichaje. El teletrabajador ficha
 * igual (Ley 10/2021 art. 14), pero no está en ningún centro y no se le pide
 * ubicación: se acredita el tiempo, no el domicilio.
 */
export type WorkMode = 'ONSITE' | 'REMOTE';

export type EventType =
  | 'PUNCH_IN'
  | 'PUNCH_OUT'
  | 'BREAK_START'
  | 'BREAK_END';

export type BreakReason =
  | 'COFFEE'
  | 'SMOKE'
  | 'LUNCH'
  | 'PERSONAL_CALL'
  | 'ADMIN_CALL'
  | 'BATHROOM'
  | 'ERRAND'
  | 'OTHER';

export interface PunchEvent {
  id?: number;                    // Auto-generated IndexedDB key
  // De QUIÉN es este fichaje. No lo llevaba, y la cola no filtraba: en un móvil
  // compartido —obra, hostelería— el fichaje de A lo subía la sesión de B con
  // SU token, y el servidor lo atribuía a B. Dos jornadas falsas en el registro
  // sin que nadie hubiera atacado nada.
  userId: number;
  clientEventId: string;          // UUID for idempotency
  eventType: EventType;
  timestamp: Date;
  breakReason?: BreakReason;      // For BREAK_START
  worksiteUuid?: string;          // Centro de trabajo: el UUID del QR escaneado al fichar
  qrToken?: string;               // Token TOTP del QR de pantalla (si el centro lo usa)
  workMode?: WorkMode;            // Presencial (por defecto) o a distancia

  // Location data
  latitude?: number;
  longitude?: number;
  accuracy?: number;              // GPS accuracy in meters

  // Device info
  deviceFingerprint: string;

  // Antifraude del dispositivo en el momento del fichaje (ronda 3,
  // r2b18[10]): MOCK_LOCATION, EMULATOR_DETECTED, TIMEZONE_MISMATCH,
  // IMPOSSIBLE_SPEED, LOW_ACCURACY… se calculaban en el único sitio que
  // podía verlas (el dispositivo) y morían en un `console.log`; ahora se
  // guardan con el evento y viajan al servidor en el sync. Campo normal de
  // Dexie, SIN índice: no toca el esquema de ninguna versión.
  fraudFlags?: string[];

  // Sync status
  syncStatus: SyncStatus;
  syncError?: string;
  syncAttempts: number;
  lastSyncAttempt?: Date;
  serverEventId?: number;         // ID returned from server after sync
  // El veredicto del SERVIDOR sobre esta marca. `synced` sólo dice "llegó";
  // si entró PENDING_APPROVAL (geovalla sin comprobar, token de pantalla
  // caducado, retrodatación…), la jornada NO cuenta hasta que un responsable
  // la apruebe — y el trabajador tiene que saberlo (ronda 3, r2b02/r2b18).
  serverReview?: 'PENDING_APPROVAL';
  reviewReason?: string;

  // Metadata
  createdAt: Date;
}

export interface CachedUser {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  tenantId: number;
  lastSynced: Date;
}

export interface CachedWorksite {
  id: number;
  name: string;
  code: string;
  latitude?: number;
  longitude?: number;
  geofenceRadius?: number;
  isActive: boolean;
  lastSynced: Date;
}

export interface DeviceInfo {
  id?: number;
  fingerprint: string;
  userId?: number;
  boundAt?: Date;
  lastUsed: Date;
}

export interface AppState {
  key: string;
  value: any;
}

// =============================================================================
// Database Class
// =============================================================================

export class KakuatDatabase extends Dexie {
  // Tables
  punchEvents!: Table<PunchEvent>;
  cachedUsers!: Table<CachedUser>;
  cachedWorksites!: Table<CachedWorksite>;
  deviceInfo!: Table<DeviceInfo>;
  appState!: Table<AppState>;

  constructor() {
    super('kakuat-db');

    // Schema versioning - never delete versions, only add
    this.version(1).stores({
      // Indexes: ++id = auto-increment, & = unique
      punchEvents: '++id, clientEventId, eventType, timestamp, syncStatus, [syncStatus+createdAt]',
      cachedUsers: 'id, email',
      cachedWorksites: 'id, code, isActive',
      deviceInfo: '++id, fingerprint, userId',
      appState: 'key'
    });

    // v2: el fichaje lleva usuario, y se consulta SIEMPRE por él.
    // Los eventos que ya estuvieran en el dispositivo sin usuario se marcan con
    // userId 0, que no es de nadie: no se subirán con la sesión del siguiente.
    // Perder un pendiente huérfano es mucho menos malo que atribuírselo a quien
    // no lo hizo.
    this.version(2).stores({
      punchEvents: '++id, clientEventId, userId, eventType, timestamp, syncStatus, [userId+syncStatus]',
      cachedUsers: 'id, email',
      cachedWorksites: 'id, code, isActive',
      deviceInfo: '++id, fingerprint, userId',
      appState: 'key'
    }).upgrade(async (tx) => {
      await tx.table('punchEvents').toCollection().modify((evento: PunchEvent) => {
        if (evento.userId === undefined) evento.userId = 0;
      });
    });
  }
}

// Singleton instance
export const db = new KakuatDatabase();

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Generate a UUID for event identification.
 * Used for idempotency - server won't process same event twice.
 */
export function generateEventId(): string {
  return crypto.randomUUID();
}

/**
 * Deja la huella del dispositivo donde el interceptor de http.ts la lee para
 * la cabecera `X-Device-Fingerprint`. La clave existía y nadie la escribía:
 * la cabecera salía vacía en TODAS las peticiones (ronda 4, r2b18[4]).
 * Try/catch porque el storage puede estar bloqueado y la huella es control
 * blando: no puede tumbar un fichaje.
 */
export function publicarHuellaDispositivo(fingerprint: string): void {
  try {
    localStorage.setItem('kakuat_device_fingerprint', fingerprint);
  } catch {
    /* storage bloqueado: la cabecera saldrá vacía, nada más */
  }
}

/**
 * Get or create device fingerprint.
 * This helps detect if someone tries to punch from a different device.
 */
export async function getDeviceFingerprint(): Promise<string> {
  let device = await db.deviceInfo.orderBy('id').first();

  if (!device) {
    const fingerprint = generateEventId();
    await db.deviceInfo.add({
      fingerprint,
      lastUsed: new Date()
    });
    publicarHuellaDispositivo(fingerprint);
    return fingerprint;
  }

  // Update last used
  await db.deviceInfo.update(device.id!, { lastUsed: new Date() });
  publicarHuellaDispositivo(device.fingerprint);
  return device.fingerprint;
}

/**
 * Create a new punch event in local database.
 * Returns immediately - sync happens in background.
 */
export async function createPunchEvent(
  userId: number,
  eventType: EventType,
  options: {
    breakReason?: BreakReason;
    worksiteUuid?: string;
    qrToken?: string;
    workMode?: WorkMode;
    latitude?: number;
    longitude?: number;
    accuracy?: number;
    fraudFlags?: string[];
  } = {}
): Promise<PunchEvent> {
  const fingerprint = await getDeviceFingerprint();

  // Clamp de la precisión GPS antes de crear el evento: el schema del lote
  // del backend rechaza accuracy fuera de rango con un 422 que tumba el
  // LOTE entero (hasta 50 fichajes buenos), y la Geolocation API entrega
  // decenas de kilómetros cuando posiciona por WiFi/celda. La precisión
  // mala es física, no fraude — doctrina del producto: no se bloquea, se
  // marca. El valor real ya queda delatado por la flag LOW_ACCURACY, que
  // se calcula ANTES de este clamp con el dato crudo (ronda 4, r2b02[1]).
  const ACCURACY_MAXima_QUE_ADMITE_EL_LOTE = 10000;

  const event: Omit<PunchEvent, 'id'> = {
    userId,
    clientEventId: generateEventId(),
    eventType,
    timestamp: new Date(),
    breakReason: options.breakReason,
    worksiteUuid: options.worksiteUuid,
    qrToken: options.qrToken,
    workMode: options.workMode ?? 'ONSITE',
    latitude: options.latitude,
    longitude: options.longitude,
    accuracy:
      options.accuracy != null
        ? Math.min(Math.round(options.accuracy), ACCURACY_MAXima_QUE_ADMITE_EL_LOTE)
        : undefined,
    deviceFingerprint: fingerprint,
    fraudFlags: options.fraudFlags,
    syncStatus: 'pending',
    syncAttempts: 0,
    createdAt: new Date()
  };

  const id = await db.punchEvents.add(event as PunchEvent);

  return { ...event, id } as PunchEvent;
}

/**
 * Get all pending events that need to be synced.
 */
export async function getPendingEvents(userId: number): Promise<PunchEvent[]> {
  const suyos = await db.punchEvents.where('userId').equals(userId).toArray();
  return suyos.filter((e) => e.syncStatus === 'pending' || e.syncStatus === 'error');
}


/**
 * Mark events as synced after successful server response.
 */
export async function markEventsSynced(
  clientEventIds: string[],
  serverIds: Map<string, number>
): Promise<void> {
  await db.transaction('rw', db.punchEvents, async () => {
    for (const clientId of clientEventIds) {
      await db.punchEvents
        .where('clientEventId')
        .equals(clientId)
        .modify({
          syncStatus: 'synced',
          serverEventId: serverIds.get(clientId)
        });
    }
  });
}

/**
 * Mark events as error after failed sync attempt.
 *
 * `contarIntento` decide si el intento cuenta hacia el dead-letter: sólo los
 * RECHAZOS del servidor (4xx: el evento es el problema) descartan; un fallo
 * de red o un 500 dicen nada del evento en sí y NO consumen intentos — con
 * cobertura intermitente, descartar por red sería tirar fichajes válidos.
 */
export async function markEventsError(
  clientEventIds: string[],
  error: string,
  contarIntento = true
): Promise<void> {
  await db.transaction('rw', db.punchEvents, async () => {
    for (const clientId of clientEventIds) {
      await db.punchEvents
        .where('clientEventId')
        .equals(clientId)
        .modify((event: PunchEvent) => {
          event.syncError = error;
          event.lastSyncAttempt = new Date();
          if (contarIntento) {
            event.syncAttempts += 1;
            // Dead-letter honesto: tras agotar los intentos el evento pasa a
            // «discarded», deja de reintentar (desbloqueando la cola de los
            // fichajes buenos que venían detrás) y se conserva entero para
            // auditoría. La superficie está en el banner de descartados de la
            // app, con el error de cada uno y reintento manual.
            event.syncStatus =
              event.syncAttempts >= MAX_SYNC_INTENTOS ? 'discarded' : 'error';
          } else {
            event.syncStatus = 'error';
          }
        });
    }
  });
}

/**
 * Los fichajes descartados por agotar MAX_SYNC_INTENTOS: la lista que alimenta
 * la superficie de auditoría (ver el error de cada uno) y el reintento manual.
 */
export async function getDiscardedEvents(userId: number): Promise<PunchEvent[]> {
  const suyos = await db.punchEvents.where('userId').equals(userId).toArray();
  return suyos
    .filter((e) => e.syncStatus === 'discarded')
    .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
}

/**
 * Devuelve a `pending` los descartados de un usuario (reintento manual).
 *
 * Resetea `syncAttempts` a 0. Antes no se reseteaba y el botón «Reintentar»
 * no podía funcionar: los eventos volvían a la cola con 5 intentos ya
 * consumidos y al primer fallo del lote volvían TODOS a `discarded` en un
 * intento — una oportunidad que no era una oportunidad (ronda 4,
 * r2b18[3]). Con el reset, cada reintento manual es una tanda completa de
 * intentos. El evento venenoso (si sigue ahí) volverá a bloquear SU lote,
 * pero ya no arrastra a los buenos: las respuestas sin veredicto por-evento
 * no cuentan intento (ver syncService).
 */
export async function reactivarEventosDescartados(userId: number): Promise<number> {
  const descartados = (
    await db.punchEvents.where('userId').equals(userId).toArray()
  ).filter((e) => e.syncStatus === 'discarded');

  for (const evento of descartados) {
    await db.punchEvents.update(evento.id!, {
      syncStatus: 'pending',
      syncAttempts: 0,
    });
  }
  return descartados.length;
}

/**
 * Deja constancia local de las marcas que el servidor aceptó A REVISIÓN.
 *
 * "Subido" y "contando" no son lo mismo: una marca PENDING_APPROVAL no entra
 * en la jornada hasta que un responsable la apruebe. Sin esto, el evento
 * local quedaba como cualquier otro sincronizado y la pantalla seguía
 * diciendo "registrada" — el trabajador descubría semanas después que su
 * jornada no contaba (ronda 3, r2b02/r2b18).
 */
export async function marcarEventosARevision(
  veredictos: Map<string, string | undefined>
): Promise<void> {
  if (veredictos.size === 0) return;
  await db.transaction('rw', db.punchEvents, async () => {
    for (const [clientId, razon] of veredictos) {
      await db.punchEvents
        .where('clientEventId')
        .equals(clientId)
        .modify({
          serverReview: 'PENDING_APPROVAL',
          reviewReason: razon,
        });
    }
  });
}

/**
 * Get current work state from local events.
 * Used for UI display without network.
 */
export async function getCurrentState(userId: number): Promise<{
  isClockedIn: boolean;
  isOnBreak: boolean;
  breakReason?: BreakReason;
  currentWorksite?: number;
  // La modalidad de la jornada ABIERTA (la de su ENTRADA). La salida y las
  // pausas la necesitan para no adquirir GPS en una jornada REMOTE: la
  // pantalla promete "no se te pide la ubicación" y sólo la entrada lo
  // cumplía (ronda 4, r2b18[1]).
  workMode?: WorkMode;
  lastEvent?: PunchEvent;
}> {
  // Una jornada abierta, no "los eventos de hoy". Mirando sólo desde la
  // medianoche local, quien entraba a las 22:00 veía la pantalla en "FUERA" al
  // pasar de las 00:00 — y volvía a pulsar ENTRADA, con lo que el ledger se
  // quedaba con dos entradas y ninguna salida. Es el mismo defecto que tenía
  // `get_current_state` en el servidor, y se arregla con el mismo criterio:
  // hacia atrás una jornada entera. Más de un día abierto no es una jornada.
  const desde = new Date(Date.now() - 24 * 60 * 60 * 1000);

  const events = (
    await db.punchEvents.where('userId').equals(userId).toArray()
  ).filter((e) => e.timestamp >= desde);

  // Sort by timestamp
  events.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

  let isClockedIn = false;
  let isOnBreak = false;
  let breakReason: BreakReason | undefined;
  let currentWorksite: number | undefined;
  let workMode: WorkMode | undefined;
  let lastEvent: PunchEvent | undefined;

  for (const event of events) {
    lastEvent = event;

    switch (event.eventType) {
      case 'PUNCH_IN':
        isClockedIn = true;
        isOnBreak = false;
        workMode = event.workMode ?? 'ONSITE';
        break;
      case 'PUNCH_OUT':
        isClockedIn = false;
        isOnBreak = false;
        currentWorksite = undefined;
        workMode = undefined;
        break;
      case 'BREAK_START':
        isOnBreak = true;
        breakReason = event.breakReason;
        currentWorksite = undefined;
        break;
      case 'BREAK_END':
        isOnBreak = false;
        breakReason = undefined;
        break;
    }
  }

  return { isClockedIn, isOnBreak, breakReason, currentWorksite, workMode, lastEvent };
}

/**
 * Clear old synced events (keep last 30 days).
 */
export async function cleanupOldEvents(): Promise<number> {
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

  const deleted = await db.punchEvents
    .where('syncStatus')
    .equals('synced')
    .and(event => event.timestamp < thirtyDaysAgo)
    .delete();

  console.log(`[DB] Cleaned up ${deleted} old events`);
  return deleted;
}

/**
 * Borra del navegador lo que es de la sesión que se cierra.
 *
 * `logout()` sólo reseteaba el estado en memoria: los fichajes pendientes, el
 * estado de jornada y los centros cacheados sobrevivían al cambio de usuario.
 * El siguiente en entrar veía "TRABAJANDO" con los eventos del anterior y, si
 * era de otra empresa, los nombres y las coordenadas de los centros de la
 * anterior.
 *
 * De quien se va SÓLO se borra lo ya sincronizado: lo que vive en el ledger.
 * Los pendientes y los fallidos NUNCA llegaron y borrarlos aquí era
 * destruirlos para siempre — la pantalla había dicho "registrada", la cola
 * prometía subirlos, y el fichaje dejaba de existir en cualquier sitio: hueco
 * irrecuperable en el registro del art. 34.9 (ronda 3, r2b18). Se quedan en
 * ESTE dispositivo — la cola es por usuario, no molestan al siguiente — y
 * suben cuando esa persona vuelva a entrar en él; desde otro aparato no;
 * la cola vive en el IndexedDB de esta máquina, no en el servidor.
 */
export async function clearLocalData(userId?: number): Promise<void> {
  if (userId !== undefined) {
    await db.punchEvents
      .where('userId')
      .equals(userId)
      .and((e) => e.syncStatus === 'synced')
      .delete();
  }
  await db.cachedWorksites.clear();
  await db.cachedUsers.clear();

  // Y las cachés del service worker (ronda 4, r2b24[2]): los alias
  // `api-cache`/`workbox` pueden tener respuestas de la API de quien acaba
  // de salir. En un dispositivo compartido (la tablet del centro, el móvil
  // de obra) la jornada de A no puede quedar servible para B sin petición.
  // Nunca debe fallar el logout por aquí: si el navegador no soporta Cache
  // Storage, no había nada que borrar.
  if (typeof window !== 'undefined' && 'caches' in window) {
    try {
      const nombres = await caches.keys();
      await Promise.all(
        nombres
          .filter((k) => k.startsWith('api-cache') || k.startsWith('workbox'))
          .map((k) => caches.delete(k))
      );
    } catch {
      /* caché inaccesible: el logout no se detiene por esto */
    }
  }
}

/**
 * Devuelve a la cola lo que se quedó a medias.
 *
 * Antes del POST el lote se marca `syncing`. Si la petición muere ahí —red que
 * se corta, pestaña que el sistema descarta, servidor que no contesta— nadie lo
 * devolvía a `pending`: esos fichajes no se reintentaban NUNCA y tampoco salían
 * en el contador de "en cola", así que desaparecían en silencio. En un registro
 * legal, un fichaje que se pierde sin ruido es un hueco que la empresa no puede
 * reconstruir.
 *
 * Se llama al arrancar el sync, que es cuando se sabe que ninguna subida está
 * viva de verdad.
 */
export async function recuperarEventosAtascados(userId: number): Promise<number> {
  const atascados = (
    await db.punchEvents.where('userId').equals(userId).toArray()
  ).filter((e) => e.syncStatus === 'syncing');

  for (const evento of atascados) {
    await db.punchEvents.update(evento.id!, { syncStatus: 'pending' });
  }
  return atascados.length;
}
